import * as PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import {assert} from 'Asserts';
import notifications from 'notifications';
import {BenchlingDispatch} from 'redux-stores/Store';
import FormGroup from 'ui-library/bs/FormGroup';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Modal from 'ui-library/modals/Modal';

import {validatableInputsToReadable} from './ValidatableInput';
import {
  AssayValidationComment,
  STATUS_TO_NAME,
  VALIDATABLE_INPUT_SHAPE,
  ValidatableInput,
  WritableAssayValidationStatus,
} from './ValidationConstants';
import {bulkSetAssayValidation} from './ValidationReduxActions';

interface ModalProps {
  comment: AssayValidationComment;
  inputsReadableInButton: string;
  inputsReadableInTitle: string;
  isSaving: boolean;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  onSetComment: (comment: AssayValidationComment) => void;
  onSetStatus: (status: WritableAssayValidationStatus) => void;
  status: WritableAssayValidationStatus;
  warningMessage: string | null;
}

/**
 * Presentational SetValidationModal component
 */
function SetValidationModal(props: ModalProps): JSX.Element {
  return (
    <Modal
      title={`Set validation on ${props.inputsReadableInTitle}`}
      medium={true}
      closeable={!props.isSaving}
      scrollable={true}
      onRequestClose={props.onRequestClose}
      onRequestSubmit={props.onRequestSubmit}
      buttons={[
        {
          disabled: props.isSaving,
          text: props.isSaving ? 'Updating...' : `Update ${props.inputsReadableInButton}`,
          style: ButtonStyle.SECONDARY,
          submit: true,
          dataTestAttrs: {component: 'SetValidationModal', element: 'submit'},
        },
      ]}
    >
      <FormGroup>
        <FieldLabel label="Validation status" />
        <div>
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.LARGE}>
                <Option
                  onSelect={() => {
                    props.onSetStatus(WritableAssayValidationStatus.VALID);
                  }}
                >
                  {STATUS_TO_NAME[WritableAssayValidationStatus.VALID]}
                </Option>
                <Option
                  onSelect={() => {
                    props.onSetStatus(WritableAssayValidationStatus.INVALID);
                  }}
                >
                  {STATUS_TO_NAME[WritableAssayValidationStatus.INVALID]}
                </Option>
              </Menu>
            }
          >
            <Button
              isDropdown={true}
              buttonStyle={ButtonStyle.TERTIARY_GRAY}
              size={ButtonSize.SMALL}
              text={STATUS_TO_NAME[props.status]}
              dataTestAttrs={{component: 'ValidationDropdown', element: 'button'}}
            />
          </Dropdown>
        </div>
      </FormGroup>
      <FormGroup>
        <FieldLabel isRequired={true} label="Comment" />
        <textarea
          autoFocus={true}
          value={props.comment || ''}
          onChange={(e) => {
            props.onSetComment(e.target.value);
          }}
          data-test-component="ValidationComment"
          data-test-element="comment"
        />
      </FormGroup>
      {props.warningMessage && <p className="alert alert-warning">{props.warningMessage}</p>}
    </Modal>
  );
}
SetValidationModal.displayName = 'SetValidationModal';

interface DispatchProps {
  onBulkSetAssayValidation: (
    status: WritableAssayValidationStatus,
    comment: AssayValidationComment
  ) => Promise<void>;
}
interface OwnProps {
  onRequestClose: () => void;
  validatableInputs: Array<ValidatableInput>;
  warningMessage: string | null;
}
type Props = OwnProps & DispatchProps;

interface ConnectedModalState {
  comment: AssayValidationComment;
  isSaving: boolean;
  status: WritableAssayValidationStatus;
}

class ConnectedSetValidationModal extends React.Component<Props, ConnectedModalState> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static propTypes: any = {
    onBulkSetAssayValidation: PropTypes.func.isRequired,
    onRequestClose: PropTypes.func.isRequired,
    validatableInputs: PropTypes.arrayOf(VALIDATABLE_INPUT_SHAPE.isRequired).isRequired,
  };

  state: ConnectedModalState = {
    comment: null,
    isSaving: false,
    status: WritableAssayValidationStatus.INVALID,
  };

  _handleSubmit = async (): Promise<void> => {
    if (!this.state.comment) {
      notifications.error('Please enter a comment.');
      return;
    }

    this.setState({isSaving: true});
    try {
      await this.props.onBulkSetAssayValidation(this.state.status, this.state.comment);
    } finally {
      this.setState({isSaving: false});
      this.props.onRequestClose();
    }
  };

  render(): JSX.Element {
    assert(this.props.validatableInputs.length > 0, 'Expected non-empty validatableInputs');
    const inputsReadable = validatableInputsToReadable(this.props.validatableInputs, false);
    const inputsReadableWithCounts = validatableInputsToReadable(this.props.validatableInputs, true);
    return (
      <SetValidationModal
        comment={this.state.comment}
        inputsReadableInButton={
          // Omit count if only 1 ValidatableInput: 'Update 1 run' -> 'Update run'
          this.props.validatableInputs.length === 1 ? inputsReadable : inputsReadableWithCounts
        }
        inputsReadableInTitle={inputsReadable}
        isSaving={this.state.isSaving}
        onRequestClose={this.props.onRequestClose}
        onRequestSubmit={() => {
          this._handleSubmit();
        }}
        onSetComment={(comment) => {
          this.setState({comment});
        }}
        onSetStatus={(status) => {
          this.setState({status});
        }}
        status={this.state.status}
        warningMessage={this.props.warningMessage}
      />
    );
  }
}

function mapDispatchToProps(dispatch: BenchlingDispatch, props: OwnProps): DispatchProps {
  return {
    onBulkSetAssayValidation: (
      status: WritableAssayValidationStatus,
      comment: AssayValidationComment
    ): Promise<void> => dispatch(bulkSetAssayValidation(props.validatableInputs, status, comment)),
  };
}
export default connect(null, mapDispatchToProps)(ConnectedSetValidationModal);
